<script setup>
import { get_trait, get_package, get_characteristics } from '@/services/option';
import { create_material, create_material_bulk } from '@/services/materials';
import { ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { get_terms } from '@/services/terms';
import { swalError } from '@/helper';
import { get_packages } from '@/services/option';
import { get_traits } from '@/services/traits'; // Add this line
// axios import is no longer needed here as it's moved to materials.js

const isLoading = ref(true)
const material = ref({
  name: '',
  description: '',
  material_media: null,
  reference_urls: [],
})
const selectedFile = ref(null)
const reading_materials = ref({list: [], temp: {link: '', title: ''}})
const selectedTrait = ref([])
const selectedPackage = ref('')
const traits = ref([])

const isUploading = ref(false);
onMounted(async ()=>{
  let traits_response = await get_traits()
  traits.value = generateTraitOption(traits_response.result.data)
  await getPackages()
  isLoading.value = false
})

const packageConfig = ref([])
async function getPackages(){
  let response = await get_packages()
  if (response.error){
    swalError(response.result.error, true)
    return
  }
  packageConfig.value = [
    ...response.result.map(item=>({
      valueProp: item.id,
      label: item.options.name.value,
      disabled: false
    }))
  ]
}

function generateTraitOption(reponse){
  reponse = reponse.map(item => {
    return {
      valueProp: item.id,
      label: item.name,
      disabled: false
    }
  })
  return reponse
}



function handleFileSelect(event) {
  const file = event.target.files[0];
  if (file) {
    selectedFile.value = file;
    material.value.material_media = file.name; // Keep for display if needed
  }
}

function addReading(){
  if (reading_materials.value.temp.link && reading_materials.value.temp.title) {
    reading_materials.value.list.push({
      title: reading_materials.value.temp.title,
      link: reading_materials.value.temp.link
    });
    reading_materials.value.temp = {link: '', title: ''};
  } else {
    swalError('Please fill in both file name and link.');
  }
}

function removeReading(index){
  reading_materials.value.list.splice(index, 1)
}

async function createReading(module_id){
  let materialParam = reading_materials.value.list.map(item => {
    return {
      name: item.title,
      material_type: 'reading_material',
      description: item.link, // Store link in description
      material_relationships_attributes: [{
        object_id: parseInt(module_id),
        object_type: 'material',
      }]
    }
  });

  let storeResponse = [];
  await Promise.all(materialParam.map(async (item) => {
    let response = await create_material(item);
    if (response.error){
      storeResponse.push(response.result.message);
    }
  }));
  let hasError = storeResponse.some(item => item.error == true);
  return {error: hasError ? true : false};
}

const temp_url = ref({name: '', url: ''})
function addUrl(){
  material.value.reference_urls.push(temp_url.value)
  temp_url.value = {name: '', url: ''}
}

function removeUrl(index){
  material.value.reference_urls.splice(index, 1)
}

const isSubmitting = ref(false)
async function createMaterial(){
  isSubmitting.value = true;

  // Validate main module PDF upload
  if (!selectedFile.value) {
    swalError('Please upload the main module PDF.');
    isSubmitting.value = false;
    return;
  }

  // Validate reading materials uploads
  const allReadingMaterialsValid = reading_materials.value.list.every(item => item.link && item.title);
  if (!allReadingMaterialsValid) {
    swalError('Please ensure all reading materials have a title and link.');
    isSubmitting.value = false;
    return;
  }

  const formData = new FormData();
  formData.append('material[name]', material.value.name);
  formData.append('material[description]', JSON.stringify(material.value.reference_urls));
  formData.append('material[material_type]', 'module');
  formData.append('material[material_media]', selectedFile.value);

  const relationships = [...serialTrait(), ...serialPackage()];
  relationships.forEach((rel, index) => {
    formData.append(`material[material_relationships_attributes][${index}][object_id]`, rel.object_id);
    formData.append(`material[material_relationships_attributes][${index}][object_type]`, rel.object_type);
  });
  
  // materialParams is now formData
  let response = await create_material(formData);
  if (response.error){
    swalError(response.result.message);
    isSubmitting.value = false;
    return;
  }

  let readingResponse = await createReading(response.result.data.data.id);
  if (readingResponse.error){
    swalError(response.result.message);
    isSubmitting.value = false;
    return;
  }

  Swal.fire({
    title: 'Created',
    text: "Module has been created",
    icon: 'success'
  });
  isSubmitting.value = false;
}

function serialTrait(){
  return selectedTrait.value.map(item => {
    return {
      object_id: item,
      object_type: 'trait'
    }
  })
}

function serialPackage(){
  return [{
    object_id: selectedPackage.value,
    object_type: 'package'
  }]
}

const isLoadingTrait = ref(true)
async function updateTraitSelection(value, select){
  let response = await get_characteristics(value)
  if (response.error){
    swalError(response.result.error)
    return
  }
  traits.value = [
    ...response.result.included.map(item => ({
      valueProp: item.id,
      label: item.attributes.name,
      disabled: false
    }))
  ]
  isLoadingTrait.value = false
}
</script>

<template>
  <div class="container-fluid">
    <Breadcrumbs main="New Module" title="New Module"/>

    <LoadingCard v-if="isLoading" />
    <template v-else>
      <!-- Page title -->
      <div class="d-flex align-items-center">
        <div>
          <h4 class="mb-0">Add New Module</h4>
        </div>
      </div>

      <div class="row mt-3">
        <div class="col-9 card">
          <div class="card-body">
            <div class="form-items mt-3">
              <!-- Title -->
              <div class="row">
                <div class="col-5">
                  <label class="form-label" for="name">Name</label>
                  <input v-model="material.name" class="form-control" id="name" type="text" placeholder="Module Name" required>
                </div>
              </div>
              <!-- PDF Module -->
              <div class="mt-4 row">
                <label class="form-label" for="age">PDF Module</label>
                <div class="row">
                  <div class="col-md-9">
                    <input
                      class="form-control"
                      type="file"
                      aria-label="dna report"
                      required=""
                      @change="handleFileSelect"
                      accept="application/pdf"
                    >
                    <p v-if="selectedFile" class="mt-2 text-success">Selected: {{ selectedFile.name }}</p>
                  </div>
                </div>
              </div>
              <!-- Reading Material -->
              <div class="mt-4 row">
                <label class="form-label" for="age">Reading Material</label>
                <div>
                  <form @submit.prevent="addReading()" class="row">
                    <div class="col-5">
                      <input v-model="reading_materials.temp.title" class="form-control" id="name" type="text" placeholder="File Name" required>
                    </div>
                    <div class="col-5">
                      <input v-model="reading_materials.temp.link" class="form-control" id="link" type="text" placeholder="File Link" required>
                    </div>
                    <div class="col-2">
                      <div class="col-2">
                        <button type="submit" class="btn-rounded-icon p-0 btn btn-success"
                        >
                          <vue-feather type="plus"></vue-feather>
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
                <div v-auto-animate class="reading-list mt-2">
                  <div v-for="(item, i) in reading_materials.list" class="row my-2">
                    <div class="col-5">
                      <input v-model="item.title" class="form-control" id="name" type="text" placeholder="File Name" required>
                    </div>
                    <div class="col-5">
                      <input v-model="item.link" class="form-control" id="link" type="text" placeholder="File Link" required>
                    </div>
                    <div class="col-2">
                      <div class="col-2">
                        <button @click="removeReading(i)" class="btn-rounded-icon p-0 btn btn-danger"
                        >
                          <vue-feather type="x"></vue-feather>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- URL -->
              <div v-auto-animate class="mt-4">
                <div>
                  <label class="form-label">Reference URL</label>
                </div>
                <form @submit.prevent="addUrl()" class="row">
                  <div class="col-6">
                    <input class="form-control" 
                      type="text" 
                      placeholder="Title" required
                      v-model="temp_url.name"
                    >
                  </div>
                  <div class="col-4">
                    <input class="form-control" 
                      type="text" 
                      placeholder="Reference URL" required
                      v-model="temp_url.url"
                    >
                  </div>
                  <div class="col-2">
                    <button type="submit" class="btn-rounded-icon p-0 btn btn-success"
                    >
                      <vue-feather type="plus"></vue-feather>
                    </button>
                  </div>
                </form>
                <!-- items -->
                <div v-for="(reference_url, index) in material.reference_urls" class="row mt-2">
                  <div class="col-6">
                    <input class="form-control" 
                      type="text" 
                      placeholder="Reference Url" required
                      :value="reference_url.name"
                    >
                  </div>
                  <div class="col-4">
                    <input class="form-control" 
                      type="text" 
                      placeholder="Reference Url" required
                      :value="reference_url.url"
                    >
                  </div>
                  <div class="col-2">
                    <button class="btn-rounded-icon p-0 btn btn-danger"
                      @click="removeUrl(index)"
                    >
                      <vue-feather type="x"></vue-feather>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Submit & Trait Selection -->
        <div class="col-3">
          <div class="card">
            <div class="card-body">

              <!-- Package Selection -->
              <div class="row mb-3">
                <label class="form-label" for="age_group_from">Packages</label>
                <multiselect 
                  style="padding-left: 0;"
                  v-model="selectedPackage" 
                  placeholder="Select Package"
                  :options="packageConfig"
                  valueProp="valueProp"
                  @change="updateTraitSelection"
                ></multiselect>
              </div>
              
              <!-- Trait Selection -->
              <div class="row mb-3">
                <label class="form-label" for="age_group_from">Trait</label>
                <multiselect 
                  :disabled="isLoadingTrait"
                  style="padding-left: 0;"
                  v-model="selectedTrait" 
                  placeholder="Select trait"
                  :options="traits"
                  :mode="'tags'"
                  valueProp="valueProp"
                  searchable="true"
                  :hideSelected="true"
                ></multiselect>
              </div>
              
              <!-- submit btn -->
              <button class="btn btn-light-success float-end"
                @click="createMaterial()"
                :disabled="isSubmitting || isUploading"
              >
                <div v-if="isSubmitting || isUploading" class="spinner-border spinner-border-sm" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
                <template v-else>
                  Create Module
                </template>
              </button>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.form-label{
  font-weight: 500;
}
</style>